import { useState, useCallback } from "react";
import axios from "axios";
import { attendanceError } from "../../components/attendance/attendanceI18n";

const getCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const initialPayrollEmployeeForm = {
  name: "",
  employee_code: "",
  department: "",
  employee_type: "staff",
  linked_account_type: "",
  linked_account_id: "",
  phone: "",
  hire_date: "",
  leave_date: "",
  base_salary: "",
  allowances_text: "",
  dependent_count: 0,
  standard_monthly_remuneration: "",
  resident_tax: "",
  care_insurance_applicable: false,
  social_insurance_prefecture: "chiba",
  tax_category: "kou",
  employment_insurance_category: "general",
  payroll_notes: "",
  is_active: true,
};

const initialCompanySettingsForm = {
  company_name: "湘南国際株式会社",
  company_address: "〒286-0221 千葉県富里市七栄532-296",
  company_phone: "050 6866 2392",
  company_number: "T5040001133939",
  representative_name: "",
  stamp_text: "",
  seal_data_url: "",
};

const getDownloadFilename = (response, fallbackName) => {
  const disposition = response.headers?.["content-disposition"] || "";
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch) return decodeURIComponent(encodedMatch[1]);
  return fallbackName;
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseAmountItems = (text) => {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, amount] = line.split(/[:：,，\s]+/);
      return { label: label || "", amount: Number(amount || 0), taxable: label !== "非課税通勤費" };
    })
    .filter((item) => item.label && item.amount > 0);
};

const buildEmployeePayload = (form) => ({
  name: form.name,
  employee_code: form.employee_code || "",
  department: form.department || "",
  employee_type: form.employee_type,
  linked_account_type: form.linked_account_type || null,
  linked_account_id: form.linked_account_id || null,
  phone: form.phone || "",
  hire_date: form.hire_date || null,
  leave_date: form.leave_date || null,
  base_salary: Number(form.base_salary || 0),
  allowances: parseAmountItems(form.allowances_text),
  dependent_count: Number(form.dependent_count || 0),
  standard_monthly_remuneration: form.standard_monthly_remuneration === "" ? null : Number(form.standard_monthly_remuneration),
  resident_tax: Number(form.resident_tax || 0),
  care_insurance_applicable: Boolean(form.care_insurance_applicable),
  social_insurance_prefecture: form.social_insurance_prefecture || "chiba",
  tax_category: form.tax_category || "kou",
  employment_insurance_category: form.employment_insurance_category || "general",
  payroll_notes: form.payroll_notes || "",
  is_active: Boolean(form.is_active),
});

const toNumber = (value) => Number(String(value ?? "").replace(/[^\d.-]/g, "") || 0);

const normalizeEditableItems = (items = [], defaultTaxable = true) => (
  items
    .map((item) => ({
      label: item.label || "",
      amount: toNumber(item.amount),
      taxable: item.taxable ?? defaultTaxable,
      auto: Boolean(item.auto),
      source: item.source || "manual_editor",
    }))
    .filter((item) => item.label || item.amount > 0)
);

const buildPayslipEditorForm = (payslip) => ({
  basic_info: {
    company_name: "湘南国際株式会社",
    company_address: "〒286-0221 千葉県富里市七栄532-296",
    company_phone: "050 6866 2392",
    company_number: "T5040001133939",
    representative_name: payslip?.basic_info?.representative_name || "",
    stamp_text: "",
    employee_name: payslip?.basic_info?.employee_name || payslip?.employee_name || "",
    employee_code: payslip?.basic_info?.employee_code || payslip?.employee_code || "",
    department: payslip?.basic_info?.department || payslip?.department || payslip?.employee_type || "",
    month: payslip?.basic_info?.month || payslip?.month || "",
    payment_date: payslip?.basic_info?.payment_date || `${payslip?.month || ""}-25`,
    tax_category: payslip?.basic_info?.tax_category || "kou",
  },
  attendance: {
    work_days: payslip?.attendance?.work_days ?? "",
    attendance_days: payslip?.attendance?.attendance_days ?? "",
    absence_days: payslip?.attendance?.absence_days ?? "",
    work_hours: payslip?.attendance?.work_hours ?? "",
    overtime_hours: payslip?.attendance?.overtime_hours ?? "",
  },
  earnings: (payslip?.earnings || [{ label: "基本給", amount: 0, taxable: true }]).map((item) => ({ ...item, amount: String(item.amount ?? "") })),
  deductions: (payslip?.deductions || []).map((item) => ({ ...item, amount: String(item.amount ?? "") })),
  notes: payslip?.notes || "",
});

export default function useAdminPayroll({
  apiBaseUrl,
  getErrorMessage,
  payrollApiPath = "/admin/payroll",
  driverImportApiPath = "/admin/drivers",
  authTokenKey = "token",
}) {
  const [payrollEmployees, setPayrollEmployees] = useState([]);
  const [annualPayroll, setAnnualPayroll] = useState(null);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedPayrollRun, setSelectedPayrollRun] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(getCurrentMonthValue());
  const [payrollYear, setPayrollYear] = useState(getCurrentMonthValue().slice(0, 4));
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [driverImportOptions, setDriverImportOptions] = useState([]);
  const [showPayrollEmployeeForm, setShowPayrollEmployeeForm] = useState(false);
  const [editingPayrollEmployee, setEditingPayrollEmployee] = useState(null);
  const [payrollEmployeeForm, setPayrollEmployeeForm] = useState(initialPayrollEmployeeForm);
  const [companySettingsForm, setCompanySettingsForm] = useState(initialCompanySettingsForm);
  const [editingPayslip, setEditingPayslip] = useState(null);
  const [payslipEditorForm, setPayslipEditorForm] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem(authTokenKey);
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const attendanceRequest = useCallback(async (method, path, data, params = {}) => {
    const token = localStorage.getItem(authTokenKey);
    return (await axios.request({ method, url: `${apiBaseUrl}${payrollApiPath}/attendance${path}`, data, params,
      headers: token ? { Authorization: `Bearer ${token}` } : {} })).data;
  }, [apiBaseUrl, payrollApiPath, authTokenKey]);

  const fetchPayrollEmployees = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/employees`, getAuthConfig());
      setPayrollEmployees(response.data || []);
    } catch (error) {
      console.error("获取员工工资档案失败:", error);
      setPayrollEmployees([]);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/company-settings`, getAuthConfig());
      setCompanySettingsForm({ ...initialCompanySettingsForm, ...(response.data || {}) });
    } catch (error) {
      console.error("获取公司給与设置失败:", error);
    }
  };

  const fetchPayrollRuns = async (month = payrollMonth) => {
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/runs`, {
        ...getAuthConfig(),
        params: month ? { month } : {},
      });
      setPayrollRuns(response.data || []);
    } catch (error) {
      console.error("获取工资批次失败:", error);
      setPayrollRuns([]);
    }
  };

  const fetchAnnualPayroll = async (year = payrollYear) => {
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/annual`, {
        ...getAuthConfig(),
        params: { year },
      });
      const annualData = response.data || null;
      setAnnualPayroll(annualData);
      if (annualData?.company) {
        setCompanySettingsForm({ ...initialCompanySettingsForm, ...annualData.company });
      }
      return annualData;
    } catch (error) {
      console.error("获取年度工资台账失败:", error);
      setAnnualPayroll(null);
      return null;
    }
  };

  const fetchDriverImportOptions = async () => {
    if (!driverImportApiPath) {
      setDriverImportOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${apiBaseUrl}${driverImportApiPath}`, getAuthConfig());
      const activeDriverImportOptions = (response.data || []).filter((driver) => driver.is_active !== false);
      setDriverImportOptions(activeDriverImportOptions);
    } catch (error) {
      console.error("获取工资档案可导入司机失败:", error);
      setDriverImportOptions([]);
    }
  };

  const fetchPayrollRunDetail = async (runId) => {
    if (!runId) return null;
    setPayrollLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/runs/${runId}`, getAuthConfig());
      const runDetail = response.data || null;
      setSelectedPayrollRun(runDetail);
      return runDetail;
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "获取工资明细失败"));
      setSelectedPayrollRun(null);
      return null;
    } finally {
      setPayrollLoading(false);
    }
  };

  const openCreatePayrollEmployee = () => {
    setEditingPayrollEmployee(null);
    setPayrollEmployeeForm(initialPayrollEmployeeForm);
    fetchDriverImportOptions();
    setShowPayrollEmployeeForm(true);
  };

  const openEditPayrollEmployee = (employee) => {
    setEditingPayrollEmployee(employee);
    setPayrollEmployeeForm({
      ...initialPayrollEmployeeForm,
      ...employee,
      linked_account_type: employee.linked_account_type || "",
      linked_account_id: employee.linked_account_id || "",
      hire_date: employee.hire_date || "",
      leave_date: employee.leave_date || "",
      base_salary: employee.base_salary ?? "",
      standard_monthly_remuneration: employee.standard_monthly_remuneration ?? "",
      resident_tax: employee.resident_tax ?? "",
      allowances_text: (employee.allowances || []).map((item) => `${item.label} ${item.amount}`).join("\n"),
    });
    setShowPayrollEmployeeForm(true);
  };

  const closePayrollEmployeeForm = () => {
    setEditingPayrollEmployee(null);
    setShowPayrollEmployeeForm(false);
    setPayrollEmployeeForm(initialPayrollEmployeeForm);
  };

  const handleSavePayrollEmployee = async () => {
    if (!payrollEmployeeForm.name || Number(payrollEmployeeForm.base_salary || 0) <= 0) {
      alert("请填写员工姓名和基本給");
      return;
    }
    setPayrollLoading(true);
    try {
      const payload = buildEmployeePayload(payrollEmployeeForm);
      if (editingPayrollEmployee) {
        await axios.put(`${apiBaseUrl}${payrollApiPath}/employees/${editingPayrollEmployee.id}`, payload, getAuthConfig());
      } else {
        await axios.post(`${apiBaseUrl}${payrollApiPath}/employees`, payload, getAuthConfig());
      }
      closePayrollEmployeeForm();
      await fetchPayrollEmployees();
      await fetchAnnualPayroll(payrollYear);
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "保存员工工资档案失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleGeneratePayrollRun = async (monthOverride = payrollMonth) => {
    const targetMonth = typeof monthOverride === "string" ? monthOverride : payrollMonth;
    if (!window.confirm(`${targetMonth} の給与明細草稿を生成しますか？上月已確定的明細会自动复制。`)) return;
    setPayrollLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}${payrollApiPath}/runs/generate`, {
        month: targetMonth,
        additions_by_employee: {},
        deductions_by_employee: {},
      }, getAuthConfig());
      setPayrollMonth(targetMonth);
      setPayrollYear(targetMonth.slice(0, 4));
      setSelectedPayrollRun(response.data || null);
      await fetchPayrollRuns(targetMonth);
      await fetchAnnualPayroll(targetMonth.slice(0, 4));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "生成工资草稿失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    setPayrollLoading(true);
    try {
      const response = await axios.put(`${apiBaseUrl}${payrollApiPath}/company-settings`, companySettingsForm, getAuthConfig());
      setCompanySettingsForm({ ...initialCompanySettingsForm, ...(response.data || {}) });
      if (selectedPayrollRun?.id) await fetchPayrollRunDetail(selectedPayrollRun.id);
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "保存公司信息失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const openEditPayslip = (payslip) => {
    setEditingPayslip(payslip);
    setPayslipEditorForm(buildPayslipEditorForm(payslip));
  };

  const closePayslipEditor = () => {
    setEditingPayslip(null);
    setPayslipEditorForm(null);
  };

  const handleSavePayslipAdjustment = async () => {
    if (!editingPayslip?.id || !payslipEditorForm) return;
    setPayrollLoading(true);
    try {
      const response = await axios.put(`${apiBaseUrl}${payrollApiPath}/payslips/${editingPayslip.id}/adjust`, {
        basic_info: payslipEditorForm.basic_info,
        attendance: Object.fromEntries(Object.entries(payslipEditorForm.attendance || {}).map(([key, value]) => [key, toNumber(value)])),
        earnings: normalizeEditableItems(payslipEditorForm.earnings, true),
        deductions: normalizeEditableItems(payslipEditorForm.deductions, false),
        notes: payslipEditorForm.notes || "",
      }, getAuthConfig());
      const runId = selectedPayrollRun?.id || editingPayslip.run_id;
      await fetchPayrollRunDetail(runId);
      await fetchPayrollRuns(payrollMonth);
      await fetchAnnualPayroll(payrollYear);
      setEditingPayslip(response.data || editingPayslip);
      setPayslipEditorForm(buildPayslipEditorForm(response.data || editingPayslip));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "保存給与明細失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleLockPayrollRun = async (runId = selectedPayrollRun?.id) => {
    if (!runId) return;
    if (!window.confirm("确定本月工资明细吗？确定后仍可使用“修正”功能重新打开。")) return;
    setPayrollLoading(true);
    try {
      if (
        editingPayslip?.run_id === runId
        && editingPayslip?.status !== "locked"
        && payslipEditorForm
      ) {
        await axios.put(`${apiBaseUrl}${payrollApiPath}/payslips/${editingPayslip.id}/adjust`, {
          basic_info: payslipEditorForm.basic_info,
          attendance: Object.fromEntries(Object.entries(payslipEditorForm.attendance || {}).map(([key, value]) => [key, toNumber(value)])),
          earnings: normalizeEditableItems(payslipEditorForm.earnings, true),
          deductions: normalizeEditableItems(payslipEditorForm.deductions, false),
          notes: payslipEditorForm.notes || "",
        }, getAuthConfig());
      }
      const response = await axios.post(`${apiBaseUrl}${payrollApiPath}/runs/${runId}/lock`, {}, getAuthConfig());
      const runDetail = await fetchPayrollRunDetail(response.data.id);
      await fetchPayrollRuns(payrollMonth);
      await fetchAnnualPayroll(payrollYear);
      const lockedPayslip = (runDetail?.payslips || []).find((item) => item.id === editingPayslip?.id);
      if (lockedPayslip) {
        setEditingPayslip(lockedPayslip);
        setPayslipEditorForm(buildPayslipEditorForm(lockedPayslip));
      }
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "锁定工资失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleReopenPayslip = async (payslipId = editingPayslip?.id) => {
    if (!payslipId) return;
    if (!window.confirm("将这张已确定的給与明細书转为“修正中”吗？原确定记录会保留。")) return;
    setPayrollLoading(true);
    try {
      const response = await axios.post(`${apiBaseUrl}${payrollApiPath}/payslips/${payslipId}/reopen`, {}, getAuthConfig());
      const reopenedPayslip = response.data || editingPayslip;
      await fetchPayrollRunDetail(reopenedPayslip.run_id);
      await fetchPayrollRuns(payrollMonth);
      await fetchAnnualPayroll(payrollYear);
      setEditingPayslip(reopenedPayslip);
      setPayslipEditorForm(buildPayslipEditorForm(reopenedPayslip));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "解除确定失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleExportPayrollExcel = async (runId = selectedPayrollRun?.id) => {
    if (!runId) return;
    setPayrollLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/runs/${runId}/export.xlsx`, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      triggerBlobDownload(response.data, getDownloadFilename(response, `payroll_${payrollMonth}.xlsx`));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "导出工资Excel失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleExportPayrollPdfs = async (runId = selectedPayrollRun?.id) => {
    if (!runId) return;
    setPayrollLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/runs/${runId}/export-pdfs.zip`, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      triggerBlobDownload(response.data, getDownloadFilename(response, `給与明細一括_${payrollMonth}.zip`));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "批量导出給与明細PDF失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleDownloadPayslipPdf = async (payslip) => {
    if (!payslip?.id) return;
    setPayrollLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}${payrollApiPath}/payslips/${payslip.id}/pdf`, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      triggerBlobDownload(response.data, getDownloadFilename(response, `payslip_${payslip.month}_${payslip.employee_name}.pdf`));
    } catch (error) {
      alert(error?.response?.data?.detail?.code ? attendanceError(error, 'zh_cn') : getErrorMessage(error, "导出給与明細PDF失败"));
    } finally {
      setPayrollLoading(false);
    }
  };

  return {
    attendanceRequest,
    closePayrollEmployeeForm,
    closePayslipEditor,
    annualPayroll,
    companySettingsForm,
    driverImportOptions,
    editingPayrollEmployee,
    editingPayslip,
    fetchCompanySettings,
    fetchAnnualPayroll,
    fetchDriverImportOptions,
    fetchPayrollEmployees,
    fetchPayrollRunDetail,
    fetchPayrollRuns,
    handleDownloadPayslipPdf,
    handleExportPayrollExcel,
    handleExportPayrollPdfs,
    handleGeneratePayrollRun,
    handleLockPayrollRun,
    handleReopenPayslip,
    handleSaveCompanySettings,
    handleSavePayrollEmployee,
    handleSavePayslipAdjustment,
    openCreatePayrollEmployee,
    openEditPayslip,
    openEditPayrollEmployee,
    payslipEditorForm,
    payrollEmployeeForm,
    payrollEmployees,
    payrollLoading,
    payrollMonth,
    payrollYear,
    payrollRuns,
    selectedPayrollRun,
    setPayrollEmployeeForm,
    setCompanySettingsForm,
    setPayslipEditorForm,
    setPayrollMonth,
    setPayrollYear,
    showPayrollEmployeeForm,
  };
}
